import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";

import { createApiClient } from "@/config/createApiClient";
import { selectedClientServiceIdAtom } from "@/store";

import { OrderItemQuotesApi } from "../../config/apiClient";

import { useWithTrackingMutation } from "./useWithTrackingMutation";

const orderItemQuotesApiClient = createApiClient(OrderItemQuotesApi);

export const useOrderItemQuotes = () => {
  const selectedClientServiceIdAtomValue = useAtomValue(
    selectedClientServiceIdAtom
  );

  const queryClient = useQueryClient();

  const useValidateOrderItemsQuote = () =>
    useWithTrackingMutation<
      { sandboxItemQuoteId: string; orderId: string; orderItemId: string },
      Awaited<
        ReturnType<
          typeof orderItemQuotesApiClient.orderItemQuotesControllerValidateOrderItemsQuote
        >
      >
    >(
      ({
        sandboxItemQuoteId,
        orderId,
      }: {
        sandboxItemQuoteId: string;
        orderId: string;
        orderItemId: string;
      }) =>
        orderItemQuotesApiClient.orderItemQuotesControllerValidateOrderItemsQuote(
          {
            clientServiceId: selectedClientServiceIdAtomValue,
            orderId: orderId,
            sandboxItemQuoteId,
          }
        ),
      [
        {
          name: "quote_validation",
          getMetaData: (res) => ({
            creation_time: res?.data?.createdAt,
            order_id: res?.data?.OrderItem?.[0]?.orderId ?? null,
          }),
        },
      ],
      {
        mutationKey: ["acceptSandboxItemQuotes"],
        onSuccess: (_, params) =>
          Promise.all([
            queryClient.refetchQueries({
              queryKey: ["orderItem", params.orderId, params.orderItemId],
            }),
            queryClient.refetchQueries({
              queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
            }),
            queryClient.invalidateQueries({ queryKey: ["orders"] }),
            queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
            queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          ]),
      }
    );

  const useRefuseOrderItemsQuotes = () =>
    useMutation({
      mutationKey: ["refuseSandboxItemQuotes"],
      mutationFn: (params: {
        orderId: string;
        orderItemId: string;
        command: Parameters<
          typeof orderItemQuotesApiClient.orderItemQuotesControllerRefuseOrderItemsQuotes
        >["0"]["orderItemQuotesRefuseCommand"];
      }) =>
        orderItemQuotesApiClient.orderItemQuotesControllerRefuseOrderItemsQuotes(
          {
            clientServiceId: selectedClientServiceIdAtomValue,
            orderId: params.orderId,
            orderItemQuotesRefuseCommand: params.command,
          }
        ),
      onSuccess: (_, params) => {
        void Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
        ]);
      },
    });

  const useCancelAllCurrentOrderItemQuotes = () =>
    useMutation({
      mutationKey: ["cancelAllCurrentOrderItemQuotes"],
      mutationFn: (params: {
        orderId: string;
        orderItemId: string;
        cancelReason: string;
      }) =>
        orderItemQuotesApiClient.orderItemQuotesControllerCancelAllCurrentOrderItemQuotes(
          {
            clientServiceId: selectedClientServiceIdAtomValue,
            orderId: params.orderId,
            orderItemId: params.orderItemId,
            orderItemQuoteCancelCommand: {
              cancelReason: params.cancelReason,
            },
          }
        ),
      onSuccess: (_, params) => {
        void Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
        ]);
      },
    });

  return {
    useValidateOrderItemsQuote,
    useRefuseOrderItemsQuotes,
    useCancelAllCurrentOrderItemQuotes,
  };
};
